import { DISPATCH_STORAGE_EVENTS, IntegrationPhoneServicesResponse } from '../../../adapter-interfaces/src/IEventAdapter';
import { getLocalStorageItem } from '../../../logger/src/LocalStorage';
import { Logger } from '../../../logger/src/Logger';

export function getMultiLineLabel(lineNumber: number | null | undefined): string {
  // Check if lineNumber is null or undefined
  if (lineNumber === null || lineNumber === undefined || isNaN(lineNumber)) {
    return ''; // Return empty string
  }

  // Check if lineNumber is within the expected range (1 to 50)
  if (lineNumber >= 1 && lineNumber <= 50) {
    return `L${lineNumber}`; // Return L1, L2, L3, ..., L50
  }

  // If lineNumber is outside the range, return an empty string (optional)
  return '';
}

/**
 * @function getMultiLineStyle
 * @description Extracts the last number from the line string using a regular expression (\d+)$.
 * Calculates the last digit of the number using parseInt and % 10. The last digit is used in
 * a switch statement to assign a specific color.
 * 
 * @param {string} lineNumber - The line string from which the last number is extracted.
 * @returns {object} - Style object with the assigned color based on the last digit.
 */

export const getMultiLineStyle = (lineNumber: string): React.CSSProperties => {
  const style: React.CSSProperties = {};

  // Regex to extract the last digit from the line (e.g., L1, L11, L21, etc.)
  const match = lineNumber.match(/(\d+)$/);
  const lastDigit = match ? parseInt(match[1], 10) % 10 : null;

  if (lastDigit !== null) {
    switch (lastDigit) {
      case 1:
        style.color = "var(--mds-color-theme-text-team-mint-normal)";
        break;
      case 2:
        style.color = "var(--mds-color-theme-text-team-cobalt-normal)";
        break;
      case 3:
        style.color = "var(--mds-color-theme-text-team-orange-normal)";
        break;
      case 4:
        style.color = "var(--mds-color-theme-text-team-pink-normal)";
        break;
      case 5:
        style.color = "var(--mds-color-theme-text-team-lime-normal)";
        break;
      case 6:
        style.color = "var(--mds-color-theme-text-team-cyan-normal)";
        break;
      case 7:
        style.color = "var(--mds-color-theme-text-team-purple-normal)";
        break;
      case 8:
        style.color = "var(--mds-color-theme-text-team-violet-normal)";
        break;
      case 9:
        style.color = "var(--mds-color-theme-text-team-gold-normal)";
        break;
      case 0:
        style.color = "var(--mds-color-theme-text-team-slate-normal)";
        break;
      default:
        return style;
    }
  }

  return style;
};

/**
 * lineNumber.startsWith('L'): This checks if the string starts with 'L'.
 * !isNaN(Number(lineNumber.substring(1))): This ensures that the part after 'L' is a valid number.
 * lineNumber.substring(1): If the conditions are met, it extracts the portion of the string after 'L' and returns it.
 */
export const extractLineNumber = (lineNumber: string): string => {
  // Check if the lineNumber starts with 'L' and the rest is a valid number
  if (lineNumber.startsWith('L') && !isNaN(Number(lineNumber.substring(1)))) {
    // Return the number part after 'L'
    return lineNumber.substring(1);
  }

  // Return an empty string if the format is not as expected
  return '';
};

/**
 * Maps line GUID to human-readable line label (L1-L5)
 * 
 * @description
 * For WEBEXCALLING_SHARED and WEBEXCALLING sessions:
 * - The self.id field contains a line GUID
 * - This GUID corresponds to a line's GUID in the phone services data stored in localStorage
 * - We look up the line by GUID and return its formatted label based on the line's index
 * 
 * @param {string | undefined} guid - The line GUID to look up (from call record's self.id)
 * @param {IntegrationPhoneServicesResponse | null} phoneServices - Optional pre-fetched phone services data.
 *        If not provided, will read from localStorage (less performant).
 * 
 * @returns {string} The line label (e.g., 'L1', 'L2', 'L3') or empty string if:
 *          - guid is undefined/null
 *          - phone services data not available
 *          - GUID not found in phone services data
 *          - line index is invalid (NaN)
 * 
 * @example
 * // With pre-fetched phone services (recommended for performance)
 * getLineLabelFromGuid("60d929d8-abc-123", phoneServicesData) // Returns "L1" (if index = 0)
 * 
 * @example
 * // Without phone services (will read from localStorage)
 * getLineLabelFromGuid("63d0c626-def-456") // Returns "L2" (if index = 1)
 * 
 * @example
 * // Not found scenario
 * getLineLabelFromGuid("unknown-guid") // Returns ""
 * 
 * @throws Logs error via Logger.error if GUID mapping fails or index is invalid
 */
export const getLineLabelFromGuid = (
  guid: string | undefined,
  phoneServices: IntegrationPhoneServicesResponse | null = null
): string => {
  if (!guid) {
    return '';
  }
  
  try {
    const services = phoneServices || getLocalStorageItem<IntegrationPhoneServicesResponse>(DISPATCH_STORAGE_EVENTS.STORAGE_PHONE_SERVICES_DATA);
    if (!services) {
      return '';
    }
    const lines = services?.data?.payload?.lines || [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Check GUID field (uppercase) from IntegrationPhoneServicesLine interface
      if (line?.GUID === guid) {
        const lineIndex = typeof line.index === 'number' ? line.index : parseInt(line.index, 10);
        
        // Validate lineIndex is a valid number
        if (isNaN(lineIndex) || lineIndex === null || lineIndex === undefined) {
          Logger.error(`[Multiline] Invalid line index for GUID ${guid}: ${line.index}. Expected number, got ${typeof line.index}`);
          return '';
        }
        
        const lineLabel = getMultiLineLabel(lineIndex);
        return lineLabel;
      }
    }
  } catch (error) {
    Logger.error(`Error mapping GUID to line label: ${error}`);
  }
  
  return '';
};

/**
 * Gets the name of the person who managed (handled) a call on a shared/virtual line
 * 
 * @description
 * For shared and virtual lines (WEBEXCALLING_SHARED sessions):
 * - The managedById field contains the GUID of the person (not line) who actually handled the call
 * - We look up this person's GUID in the phone services data to find their name
 * - Used to display "Managed by [Name]" in the call history UI for team accountability
 * 
 * Note: This is different from getLineLabelFromGuid:
 * - getLineLabelFromGuid: Maps line GUID → line label (L1, L2, etc.)
 * - getManagedByName: Maps person GUID → person name (John Doe, etc.)
 * 
 * @param {string} managedByGuid - The GUID of the person who managed the call (from managedById field)
 * @param {any} callData - The call data object from Webex API (contains fallback name if GUID lookup fails)
 * @param {IntegrationPhoneServicesResponse | null} phoneServices - Optional pre-fetched phone services data.
 *        If not provided, will read from localStorage (less performant).
 * 
 * @returns {string} The name of the person who managed the call, or empty string if:
 *          - managedByGuid is undefined/null
 *          - phone services data not available
 *          - GUID not found in phone services data
 *          - no fallback name in callData
 * 
 * @example
 * // With pre-fetched phone services (recommended for performance)
 * getManagedByName("person-abc-123", callData, phoneServicesData) // Returns "Vijay vijay"
 * 
 * @example
 * // Without phone services (will read from localStorage)
 * getManagedByName("person-def-456", callData) // Returns "John Doe"
 * 
 * @example
 * // Not found scenario (returns empty string)
 * getManagedByName("unknown-guid", callData) // Returns ""
 * 
 * @throws Logs error via Logger.error if GUID mapping fails
 * 
 * @see getLineLabelFromGuid - Related function for mapping line GUIDs to line labels
 */
export const getManagedByName = (
  managedByGuid: string | undefined,
  callData: any,
  phoneServices: IntegrationPhoneServicesResponse | null = null
): string => {
  if (!managedByGuid || !callData) {
    return '';
  }

  try {
    const services = phoneServices || getLocalStorageItem<IntegrationPhoneServicesResponse>(DISPATCH_STORAGE_EVENTS.STORAGE_PHONE_SERVICES_DATA);
    if (services) {
      const lines = services?.data?.payload?.lines || [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line?.GUID === managedByGuid && line?.name) {
          return line.name;
        }
      }
    }
    // Fallback: Check if managedById matches self.id (the line owner)    
    if (callData?.self?.id === managedByGuid && callData?.self?.name) {
      return callData.self.name;
    }

    return '';
  } catch (error) {
    Logger.error(`[Multiline] Error getting managed by name: ${error}`);
    return '';
  }
};